import React, { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes, forwardRef } from 'react'
import { cn } from './cn'
import { useFieldContext } from './Field'

const ringClasses = (error: boolean, disabled?: boolean) =>
  cn(
    'ring-mask flex w-full rounded-md bg-field transition-opacity',
    error
      ? '[--ring-fill:var(--plug-danger)]'
      : '[--ring-fill:var(--plug-field-ring)] focus-within:[--ring-fill:var(--plug-field-ring-focus)]',
    disabled && 'opacity-50'
  )

const controlClasses = (mono?: boolean) =>
  cn(
    'w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-fg outline-none placeholder:text-fg-3',
    'disabled:cursor-not-allowed',
    mono ? 'font-mono text-[14px]' : 'font-text text-[16px]'
  )

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  leading?: ReactNode
  trailing?: ReactNode
  error?: boolean
  mono?: boolean
  /** No ring and no fill: for amount inputs that already sit inside a card. */
  bare?: boolean
  /** Class for the outer ring wrapper. `className` goes on the <input>. */
  wrapperClassName?: string
  size?: 'md' | 'lg'
}

/** Text input with the masked hairline ring (brightens on focus, danger on error). */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      leading,
      trailing,
      error,
      mono,
      bare,
      wrapperClassName,
      className,
      id,
      size = 'md',
      disabled,
      ...props
    },
    ref
  ) => {
    const field = useFieldContext()
    const invalid = error ?? field?.invalid ?? false
    return (
      <div
        className={cn(
          bare ? cn('flex w-full', disabled && 'opacity-50') : ringClasses(invalid, disabled),
          'items-center gap-2',
          bare ? 'px-0' : 'px-4',
          size === 'lg' ? 'min-h-14' : 'min-h-12',
          wrapperClassName
        )}
      >
        {leading && <span className="flex shrink-0 items-center text-fg-2">{leading}</span>}
        <input
          ref={ref}
          id={id ?? field?.id}
          aria-invalid={invalid || undefined}
          aria-describedby={field?.describedBy}
          autoComplete="off"
          disabled={disabled}
          className={cn(controlClasses(mono), 'h-full py-3', className)}
          {...props}
        />
        {trailing && <span className="flex shrink-0 items-center gap-2 text-fg-2">{trailing}</span>}
      </div>
    )
  }
)
Input.displayName = 'Input'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean
  mono?: boolean
  wrapperClassName?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error, mono, wrapperClassName, className, id, disabled, rows = 4, ...props }, ref) => {
    const field = useFieldContext()
    const invalid = error ?? field?.invalid ?? false
    return (
      <div className={cn(ringClasses(invalid, disabled), 'px-4 py-3', wrapperClassName)}>
        <textarea
          ref={ref}
          id={id ?? field?.id}
          rows={rows}
          aria-invalid={invalid || undefined}
          aria-describedby={field?.describedBy}
          disabled={disabled}
          className={cn(controlClasses(mono), 'resize-none leading-[1.5]', className)}
          {...props}
        />
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'
